import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ShieldCheck, CalendarBlank, NavigationArrow, UserCircle, Package, Lightning } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { fmtShootWhen } from "../lib/helpers";
import AvailableShoots from "./AvailableShoots";

function Pill({ active, onClick, label, count, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`relative flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${active ? "bg-[#4A0000] text-white" : "bg-white text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>
      {label}
      {count > 0 && (
        <span data-testid={`${testid}-badge`} className={`min-w-5 h-5 px-1 grid place-items-center rounded-full text-xs font-bold ${active ? "bg-[#B08D57] text-[#4A0000]" : "bg-[#4A0000] text-white"}`}>{count}</span>
      )}
    </button>
  );
}

export default function BackupShoots({ available = [], committed = [], openShoots = [], role, tier, onAddBackup, onWithdraw, onClaim, onOpen }) {
  const [tab, setTab] = useState("open");
  return (
    <div className="mb-8 border border-[#4A0000]/25 bg-[#4A0000]/[0.03] rounded-md p-5" data-testid="backups-section">
      <div className="flex items-center gap-2 mb-4" data-testid="shoots-toggle">
        <Pill testid="toggle-open-shoots" active={tab === "open"} onClick={() => setTab("open")} label="Open Shoots" count={openShoots.length} />
        <Pill testid="toggle-backups" active={tab === "backups"} onClick={() => setTab("backups")} label="Backups" count={available.length} />
      </div>

      {tab === "open" ? (
        openShoots.length === 0
          ? <p className="text-sm text-[#5C5A58] py-2" data-testid="open-shoots-empty">No open shoots to claim right now.</p>
          : <AvailableShoots items={openShoots} role={role} tier={tier} onClaim={onClaim} onOpen={onOpen} embedded />
      ) : (
        <>
          {committed.length > 0 && (
            <div className="mb-6" data-testid="backups-committed">
              <p className="text-xs uppercase tracking-wide text-[#5C5A58] mb-2">You're on standby for</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {committed.map((p, i) => (
                  <motion.div key={`${p.id}-${p.slot || "photographer"}`} data-testid={`backup-committed-${p.id}-${p.slot || "photographer"}`}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className="lux-card rounded-[5px] p-4 flex flex-col">
                    <button onClick={() => onOpen(p.id)} className="text-left w-full min-w-0">
                      <h3 className="font-display text-lg leading-tight truncate">{p.title}</h3>
                      <p className="text-xs text-[#5C5A58] flex items-center gap-1 mt-1 truncate"><MapPin size={12} weight="duotone" className="flex-shrink-0" /><span className="truncate">{p.address}</span></p>
                    </button>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#5C5A58] flex-wrap">
                      <Badge className={p.slot === "pilot" ? "bg-[#8A6D1B]/15 text-[#8A6D1B] border-[#8A6D1B]/40" : "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/25"}>{p.slot === "pilot" ? "Pilot" : "Photographer"} backup #{p.backup_position}</Badge>
                      <span className="flex items-center gap-1"><UserCircle size={14} weight="duotone" />{p.primary_name}</span>
                    </div>
                    <Button data-testid={`withdraw-backup-${p.id}-${p.slot || "photographer"}`} variant="outline" onClick={() => onWithdraw(p.id, p.slot)}
                      className="mt-3 border-[#E9DDC6] w-full">Withdraw as backup</Button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs uppercase tracking-wide text-[#5C5A58] mb-2">Available to back up near you</p>
          {available.length === 0 ? (
            <p className="text-sm text-[#5C5A58] py-2" data-testid="backups-empty">No shoots need a backup right now.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {available.map((p, i) => (
                <motion.div key={`${p.id}-${p.slot || "photographer"}`} data-testid={`backup-available-${p.id}-${p.slot || "photographer"}`}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="group lux-card rounded-[5px] p-4 flex flex-col transition-transform hover:-translate-y-0.5">
                  <button onClick={() => onOpen(p.id)} className="text-left w-full min-w-0">
                    <h3 className="font-display text-lg leading-tight truncate group-hover:text-[#4A0000] transition-colors">{p.title}</h3>
                    <p className="text-xs text-[#5C5A58] flex items-center gap-1 mt-1 truncate"><MapPin size={12} weight="duotone" className="flex-shrink-0" /><span className="truncate">{p.address}{p.zip ? ` · ${p.zip}` : ""}</span></p>
                  </button>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <Badge className={p.slot === "pilot" ? "bg-[#8A6D1B]/15 text-[#8A6D1B] border-[#8A6D1B]/40" : "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/25"}>{p.slot === "pilot" ? "Pilot slot" : "Photographer slot"}</Badge>
                    {p.package_type && (
                      <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1"><Package size={11} weight="fill" />{p.package_type}</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs mt-3 text-[#5C5A58]">
                    <span className="flex items-center gap-1"><CalendarBlank size={13} weight="duotone" />{fmtShootWhen(p.shoot_date)}</span>
                    {p.distance != null && <span className="font-mono-num text-[#8a6d0a] flex items-center gap-1"><NavigationArrow size={12} weight="duotone" />{p.distance} mi</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-[#5C5A58]">
                    <span className="flex items-center gap-1"><UserCircle size={14} weight="duotone" />Assigned: {p.primary_name}</span>
                    <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]">{p.backup_count}/2 backups</Badge>
                  </div>
                  <Button data-testid={`add-backup-${p.id}-${p.slot || "photographer"}`} onClick={() => onAddBackup(p.id, p.slot)}
                    className="mt-3 bg-[#4A0000] hover:bg-[#5C1A1A] text-white w-full">Add me as {p.slot === "pilot" ? "pilot" : "photographer"} backup</Button>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
